import axios from "axios";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  FETCH_TRAIL_QUESTIONS,
  TRIAL_COMPLETED_API_UPDATE,
} from "../../services/apiConstants";
import "./fullExam.css";

const TRIAL_DURATION = 5 * 60;

const TrialPage = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const examData = location.state || JSON.parse(localStorage.getItem("examData")) || {};
  const token = localStorage.getItem("token");
  const { mockExamId, price, mockExamDesc } = examData;

  const [questions, setQuestions] = useState([]);
  const [currentQ, setCurrentQ] = useState(0);
  const [selectedOptions, setSelectedOptions] = useState({});
  const [marked, setMarked] = useState({});
  const [completed, setCompleted] = useState(false);
  const [timeLeft, setTimeLeft] = useState(TRIAL_DURATION);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const fetchQuestions = async () => {
      setLoading(true);
      setLoadError("");

      try {
        const response = await axios.post(
          FETCH_TRAIL_QUESTIONS,
          { mockExamId },
          {
            headers: {
              Authorization: token,
              "Content-Type": "application/json",
            },
          }
        );

        const formatted = response.data.map((q) => ({
          id: q.id,
          question: q.questionText,
          options: [
            { key: "A", value: q.optionA },
            { key: "B", value: q.optionB },
            { key: "C", value: q.optionC },
            { key: "D", value: q.optionD },
          ],
          answer: (q.correctAnswer || "").toString().trim().toUpperCase(),
        }));

        setQuestions(formatted);
        setCurrentQ(0);
        setSelectedOptions({});
        setMarked({});
        setCompleted(false);
        setTimeLeft(TRIAL_DURATION);
      } catch (err) {
        console.error(err);
        setLoadError("Failed to load trial questions. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchQuestions();
  }, [mockExamId, token]);

  useEffect(() => {
    if (completed || submitting || timeLeft <= 0) return undefined;

    const timer = setInterval(() => {
      setTimeLeft((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [completed, submitting, timeLeft]);

  useEffect(() => {
    if (timeLeft === 0 && !completed && !submitting && questions.length > 0) {
      handleSubmit(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeLeft]);

  const formatTime = (seconds) => {
    const min = Math.floor(seconds / 60);
    const sec = seconds % 60;
    return `${min.toString().padStart(2, "0")}:${sec.toString().padStart(2, "0")}`;
  };

  const handleOptionSelect = (optionKey) => {
    setSelectedOptions((prev) => ({
      ...prev,
      [currentQ]: optionKey,
    }));
  };

  const clearAnswer = () => {
    setSelectedOptions((prev) => {
      const updated = { ...prev };
      delete updated[currentQ];
      return updated;
    });
  };

  const toggleMark = () => {
    setMarked((prev) => ({
      ...prev,
      [currentQ]: !prev[currentQ],
    }));
  };

  const calculateScore = () => {
    let score = 0;

    questions.forEach((q, i) => {
      if ((selectedOptions[i] || "").toUpperCase() === q.answer) {
        score++;
      }
    });

    return score;
  };

  const handleSubmit = async (forceSubmit = false) => {
    if (submitting || completed) return;

    const unanswered = questions.length - Object.keys(selectedOptions).length;
    if (!forceSubmit && unanswered > 0) {
      const shouldSubmit = window.confirm(
        `You still have ${unanswered} unanswered question${unanswered === 1 ? "" : "s"}. Submit trial now?`
      );
      if (!shouldSubmit) return;
    }

    setSubmitting(true);

    try {
      const score = calculateScore();

      await axios.post(
        TRIAL_COMPLETED_API_UPDATE,
        {
          mockExamId,
          score,
          totalQuestions: questions.length,
          correctAnswers: score,
          status: "TRIAL_COMPLETED",
          attemptType: "TRIAL",
        },
        {
          headers: {
            Authorization: token,
            "Content-Type": "application/json",
          },
        }
      );

      localStorage.removeItem("examData");
      setCompleted(true);
      navigate("/Dashboard", {
        state: {
          trialCompleted: true,
          mockExamId,
          mockExamDesc,
          price,
          score,
        },
        replace: true,
      });
    } catch (error) {
      console.error(error);
      alert("Failed to submit trial details.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="exam-state">
        <div className="exam-loader" />
        <h3>Loading your trial...</h3>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="exam-state">
        <h3>{loadError}</h3>
        <button onClick={() => window.location.reload()}>Try Again</button>
      </div>
    );
  }

  if (!questions.length) {
    return (
      <div className="exam-state">
        <h3>No questions available for this trial.</h3>
        <button onClick={() => navigate("/dashboard")}>Back to Dashboard</button>
      </div>
    );
  }

  const answeredCount = Object.keys(selectedOptions).length;
  const markedCount = Object.values(marked).filter(Boolean).length;
  const unansweredCount = questions.length - answeredCount;
  const currentQuestion = questions[currentQ];

  const getStatusClass = (index) => {
    if (index === currentQ) return "current";
    if (marked[index]) return "marked";
    if (selectedOptions[index]) return "answered";
    return "not-answered";
  };

  return (
    <div className="exam-layout exam-layout-single">
      <div className="exam-main">
        <div className="exam-header">
          <div>
            <p className="exam-eyebrow">Trial Exam</p>
            <h2>{mockExamDesc || "Trial Exam"}</h2>
          </div>
          {!completed && (
            <div className={`timer ${timeLeft <= 60 ? "danger" : ""}`}>
              {formatTime(timeLeft)}
            </div>
          )}
        </div>

        {!completed ? (
          <>
            <div className="question-progress-panel">
              <div className="question-progress-header">
                <h3>Question Progress</h3>
                <div className="question-progress-summary">
                  <span>{answeredCount} answered</span>
                  <span>{unansweredCount} unanswered</span>
                  <span>{markedCount} marked</span>
                </div>
                <div className="progress-legend">
                  <span><i className="legend-dot answered"></i>Answered</span>
                  <span><i className="legend-dot marked"></i>Marked for Review</span>
                  <span><i className="legend-dot not-answered"></i>Not Answered</span>
                  <span><i className="legend-dot current"></i>Current</span>
                </div>
              </div>

              <div className="question-progress-grid">
                {questions.map((q, index) => (
                  <button
                    key={q.id || index}
                    className={`progress-question ${getStatusClass(index)}`}
                    onClick={() => setCurrentQ(index)}
                    aria-label={`Go to question ${index + 1}`}
                  >
                    <span>{index + 1}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="question-card">
              <div className="question-topline">
                <span>Question {currentQ + 1} of {questions.length}</span>
                {marked[currentQ] && <strong>Marked for review</strong>}
              </div>

              <h3>{currentQuestion.question}</h3>

              {currentQuestion.options.map((opt) => (
                <label
                  key={opt.key}
                  className={`option ${selectedOptions[currentQ] === opt.key ? "selected" : ""}`}
                >
                  <input
                    type="radio"
                    name={`trial-question-${currentQuestion.id || currentQ}`}
                    checked={selectedOptions[currentQ] === opt.key}
                    onChange={() => handleOptionSelect(opt.key)}
                  />
                  <span className="option-key">{opt.key}</span>
                  <span>{opt.value}</span>
                </label>
              ))}

              <div className="nav-buttons">
                <button onClick={() => setCurrentQ((i) => i - 1)} disabled={currentQ === 0}>
                  Previous
                </button>

                <button
                  className="secondary-action"
                  onClick={clearAnswer}
                  disabled={!selectedOptions[currentQ]}
                >
                  Clear Answer
                </button>

                <button className="review-action" onClick={toggleMark}>
                  {marked[currentQ] ? "Unmark Review" : "Mark for Review"}
                </button>

                <button
                  onClick={() => setCurrentQ((i) => i + 1)}
                  disabled={currentQ === questions.length - 1}
                >
                  Next
                </button>
              </div>
            </div>

            <div className="submit-row">
              <span>Submit this trial when you are ready.</span>
              <button
                className="submit-btn"
                onClick={() => handleSubmit(false)}
                disabled={submitting}
              >
                {submitting ? "Submitting..." : "Submit Trial"}
              </button>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
};

export default TrialPage;
